import * as React from "react";
import { cn } from "../../../lib/utils";

export interface AvatarProps extends React.HTMLAttributes<HTMLDivElement> {
  /** User initials to display (1-2 characters) */
  initials: string;
  /** Avatar size variant */
  size?: "sm" | "md" | "lg";
  /** Visual variant */
  variant?: "default" | "primary";
}

const sizeClasses = {
  sm: "h-8 w-8 text-sm",
  md: "h-12 w-12 text-base",
  lg: "h-16 w-16 text-lg",
} as const;

const variantClasses = {
  default: "bg-bg-active text-text-link",
  primary: "bg-bg-brand-blue text-text-inverse",
} as const;

/**
 * Avatar - Circular user avatar with initials
 *
 * Displays user initials in a circular container.
 * Supports three sizes and two visual variants.
 *
 * @example
 * ```tsx
 * <Avatar initials="JD" size="md" />
 * <Avatar initials="AG" variant="primary" />
 * ```
 */
function Avatar({
  initials,
  size = "md",
  variant = "default",
  className,
  ...props
}: AvatarProps) {
  // Ensure initials are max 2 characters and uppercase
  const displayInitials = initials.slice(0, 2).toUpperCase();

  return (
    <div
      className={cn(
        "flex items-center justify-center rounded-full font-bold leading-6",
        sizeClasses[size],
        variantClasses[variant],
        className,
      )}
      aria-label={`User avatar: ${displayInitials}`}
      {...props}
    >
      {displayInitials}
    </div>
  );
}
Avatar.displayName = "Avatar";

export { Avatar };
